<template>
  <article
    v-if="product"
    ref="productCard"
    class="product prod-card"
    :class="[
      { 'is-loading': states.component !== 'ready' },
      { 'is-hoverable': secondaryImage },
      additionalClasses,
    ]"
  >
    <figure class="prod-card__media-container">
      <a
        class="prod-card__block-link"
        :href="product.url"
        :aria-labelledby="'product-title-' + product.key"
        @click="handleLinkClick"
      >
        <aside
          v-if="product.badges"
          class="prod-card__badge-container"
        >
          <component
            :is="badgeIcon"
            v-for="(badgeIcon, index) in badgeIcons"
            :key="index"
            class="prod-card__badge"
            :class="`prod-card__badge--${badgeIcon}`"
            aria-hidden="true"
          />
        </aside>

        <div
          v-if="!isTrending"
          class="prod-card__media-carousel"
        >
          <ul
            ref="mediaCarouselContainer"
            class="prod-card__media"
          >
            <li
              :id="`${product.id}-media-main`"
              ref="carouselMediaMain"
              class="prod-card__media-item"
              :class="{ 'prod-card__media-item-with-hover': secondaryImage }"
            >
              <responsive-image
                :image="primaryImage"
                min-max="180-620"
                :sizes="imageSizes"
                :loading="lazyImages ? 'lazy' : 'eager'"
              />
              <responsive-image
                v-if="secondaryImage"
                :image="secondaryImage"
                min-max="180-620"
                :sizes="imageSizes"
                :loading="lazyImages ? 'lazy' : 'eager'"
              />
            </li>
          </ul>
        </div>
        <div
          v-else
          class="prod-card__trending-media"
        >
          <media-element
            :image="trendingMedia?.image"
            :image-desktop="trendingMedia?.imageDesktop"
            :video="trendingMedia?.video"
            :video-desktop="trendingMedia?.videoDesktop"
            :video-external-url="trendingMedia?.videoExternalUrl"
            :video-external-desktop-url="trendingMedia?.videoExternalDesktopUrl"
            :autoplay-video="trendingMedia?.autoplayVideo"
            :inline-media="trendingMedia?.inlineMedia"
            :lazyload="lazyImages"
            @set-aspect-ratio="setAspectRatio"
          />
        </div>

        <btn
          v-if="showQuickAdd"
          class="prod-card__quick-add-button button--primary button--block"
          :disabled="states.productForm !== 'ready'"
          :aria-label="selectVariantButton"
          :aria-expanded="showVariantSelector"
          aria-haspopup="true"
          :icon="buttonIcon"
          @click.stop.prevent="toggleVariantSelector"
        />
      </a>
      <overlay
        v-if="shouldOverlayVariantSelector"
        :is-active="showVariantSelector"
        type="drawer-bottom"
        parent-class="product-variant-selector__overlay"
        direction="up"
        @close-overlay="toggleVariantSelector"
      >
        <template #body>
          <div class="prod-card__variant-selector-container has-overlay">
            <button
              class="prod-card__variant-close-button"
              :aria-label="closeButtonText"
              type="button"
              @click.prevent="toggleVariantSelector"
            >
              <icon-close />
            </button>

            <product-variant-selector
              class="prod-card__variant-selector"
              :context="`product-card`"
              :current-variant="currentVariant"
              :product="product"
              :should-overlay="shouldOverlayVariantSelector"
              @variant-updated="handleVariantChange"
              @close-size-dropdown="handleSizeDropdownClose"
            />
          </div>
        </template>
      </overlay>
      <transition name="slide-up">
        <div
          v-if="showVariantSelector && !shouldOverlayVariantSelector"
          class="prod-card__variant-selector-container"
        >
          <button
            class="prod-card__variant-close-button"
            :aria-label="closeButtonText"
            type="button"
            @click.prevent="toggleVariantSelector"
          >
            <icon-close />
          </button>

          <product-variant-selector
            class="prod-card__variant-selector"
            :context="`product-card`"
            :current-variant="currentVariant"
            :product="product"
            :should-overlay="shouldOverlayVariantSelector"
            @variant-updated="handleVariantChange"
            @close-size-dropdown="handleSizeDropdownClose"
          />
        </div>
      </transition>

      <transition name="fade">
        <div
          v-if="showAddedOverlay"
          class="prod-card__alert"
        >
          <div class="prod-card__alert-text">
            <span
              class="prod-card__alert-variant"
              v-text="variantHeading"
            />
            <span
              v-if="addedToCartText"
              class="prod-card__alert-label"
              v-text="addedToCartText"
            />
            <button
              v-if="viewBagLabel"
              class="button button--short-underline prod-card__alert-button"
              @click="handleCartDrawerOpen"
              v-text="viewBagLabel"
            />
          </div>
        </div>
      </transition>
    </figure>

    <header class="prod-card__header">
      <div class="prod-card__details">
        <div class="prod-card__details-top">
          <a
            class="prod-card__link"
            :href="product.url"
            @click="handleLinkClick"
          >
            <span
              v-if="showLabel"
              class="prod-card__label"
              v-text="product.label"
            />
            <h3
              :id="'product-title-' + product.key"
              class="prod-card__title"
              v-text="product.title"
            />
          </a>
          <wishlist-button
            v-if="showWishlistButton"
            class="prod-card__wishlist"
            :current-variant="currentVariant"
            :product="product"
          />
        </div>

        <product-prices
          class="prod-card__prices"
          :currency-code="currencyCode"
          :product="product"
        />

        <div
          v-show="showSwatchWrapper"
          class="prod-card__swatch-wrapper"
        >
          <button
            v-if="showSwatchButton"
            class="prod-card__swatch-button button button--underline button--no-padding"
            @click.stop.prevent="toggleSwatches"
            v-text="swatchButtonText"
          />

          <transition name="fade">
            <product-swatches
              v-if="states.swatches === 'ready'"
              class="product-card__swatches"
              :linked-styles="product.linkedStyles"
            />
          </transition>
        </div>
      </div>
    </header>
  </article>
</template>

<script>

/**
 * Component: Product Card
 * ------------------------------------------------------------------------------
 * - Global Product Card component.
 * - Styles are loaded in via theme.scss
 * @param {String} [context] - Location of the Product Card. Enables variating functionality per-location. For example: context: `collection`
 * @param {Number} [columnGrid] - Number of grid columns in the parent collection component.
 * @param {Object} [gridActiveVariantSelector] - Product Handle of the active Variant Selector in the parent components grid. Used to ensure only one selector is open at a time.
 * @param {String} [initialProduct] - Formatted Shopify product.
 * @param {Boolean} [isPlpProduct] - Indicates whether product card is being rendered on the PLP.
 * @param {Object} [trackingParameters] - Object of parameters, typically used for GTM, Bloomreach etc. tracking.
 * @param {Boolean} [trendingEnabled] - Indicates whether trending functionality is enabled for the product card.
 * @param {Boolean} [usePackshot] - If to use the packshot as the primary image.
 * @param {Boolean} [preventDrawer] - Prevent cart drawer from opening when added to the cart (product card overlay will be shown instead).
 * @param {Boolean} [hideWishlistIcon] - Whether to hide the wishlist icon.
 * @param {Boolean} [hideProductSwatches] - Whether to hide the colour swatches icon.
 * @param {String|Number} [aspectRatio] - Force / override media aspect ratio.
 *
 */
import { mapActions, mapState } from 'pinia'

import { useCartStore } from '~stores/cart'
import { useFeatureFlagStore } from '~stores/feature-flags'
import { useLocaleStore } from '~stores/locale'
import { useScreenStore } from '~stores/screen'
import { useWishlistStore } from '~stores/wishlist'

import { formatVariantTitle } from '~/helpers/format'
import { getItemProperties } from '~/helpers/product'
import { getPackShotImage } from '~/helpers/utils'
import { getProduct } from '~/core/get-product'
import { importComponent } from '~/core/mint/helpers'
import { stringToHandle } from '~/helpers/strings'
import breakpoints from '~/helpers/breakpoints'
import setDataLayerValues from '~/helpers/elevar-data-layer'

import Btn from '~components/btn/btn'
import MediaElement from '~components/media-element/media-element'
import ProductPrices from '~components/product-prices/product-prices'
import ResponsiveImage from '~components/responsive-image/responsive-image'

import Overlay from '~components/overlay/overlay'

import BadgeMint from '~icons/badge/mint.svg'
import BadgeComingSoon from '~icons/badge/coming-soon.svg'
import BadgeMvLimited from '~icons/badge/mv-limited.svg'
import BadgeMintStudio from '~icons/badge/mint-studio.svg'
import IconClose from '~icons/misc/close.svg'
import IconRight from '~icons/direction/right.svg'

export default {
  name: 'ProductCard',

  components: {
    BadgeMint,
    BadgeComingSoon,
    BadgeMvLimited,
    BadgeMintStudio,
    Btn,
    IconClose,
    IconRight,
    MediaElement,
    ResponsiveImage,
    Overlay,
    ProductPrices,
    ProductSwatches: importComponent('product-swatches'),
    ProductVariantSelector: importComponent('product-variant-selector'),
    WishlistButton: importComponent('wishlist-button'),
  },

  props: {
    context: {
      type: String,
      default: 'default',
    },
    columnGrid: {
      type: Number,
      default: 2,
    },
    gridActiveVariantSelector: {
      type: [Boolean, String],
      default: false,
    },
    initialProduct: {
      type: [Boolean, Object],
      default: false,
    },
    isPlpProduct: {
      type: Boolean,
      default: false,
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    trendingEnabled: {
      type: Boolean,
      default: false,
    },
    usePackshot: {
      type: Boolean,
      default: false,
    },
    lazyImages: {
      type: Boolean,
      default: true,
    },
    preventDrawer: {
      type: Boolean,
      default: false,
    },
    hideWishlistIcon: {
      type: Boolean,
      default: false,
    },
    hideProductSwatches: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['link-clicked', 'variant-selector-toggled'],

  data() {
    return {
      currentVariant: false,
      flags: {
        enableQuickAdd: false,
      },
      lastAddedVariantTitle: '',
      marketingDataSource: false,
      mediaCarousel: {
        firstSlideVisible: true,
        lastSlideVisible: false,
      },
      mobileDevice: !window.matchMedia(`(min-width: ${breakpoints.medium})`).matches,
      product: {
        handle: '',
        id: '',
        images: [],
        price: {},
        title: '',
        variants: [],
      },
      productCardWidth: 0,
      states: {
        component: 'loading',
        currency: 'loading',
        productForm: 'ready',
        swatches: 'hidden',
        variantSelector: 'hidden',
        addToCart: 'ready',
      },
    }
  },

  computed: {

    /**
     * Map locale state.
     */
    ...mapState(useFeatureFlagStore, {
      featureFlags: 'productCards',
    }),

    /**
     * Map locale state.
     */
    ...mapState(useLocaleStore, {
      currencyCode: 'currencyCode',
    }),

    /**
     * Map viewport state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
      viewportWidth: 'width',
    }),

    /**
     * Calculate 'Added to bag' text.
     * - Currently pulls locale strings from ShopTheLook, as it is only used there
     * @returns {String}
     */
    addedToCartText() {
      return mint.Strings('shop_the_look.added_to_cart')
    },

    /**
     * Calculate additional modifier classes.
     */
    additionalClasses() {
      if (this.context === 'default') {
        return ''
      }

      const classes = []
      classes.push(`is-${stringToHandle(this.context)}`)

      if (!this.isTrending) {
        return false
      }

      const gridSize = this.product.trendingBanner.column_width

      classes.push(
        { 'is-trending': this.isTrending },
        { 'prod-card--full-width': gridSize === 'full' },
        { 'prod-card--half-width': gridSize === 'half' },
      )

      return classes
    },

    /**
     * Calculate the badge icon to display.
     * - Map product.badges to an array of badge icons or return empty array.
     * @returns {Array}
     */
    badgeIcons() {
      if (!this.product.badges) {
        return []
      }

      return this.product.badges.map((badge) => `badge-${badge.icon}`)
    },

    /**
     * Calculate the icon for the 'Quick Add' button.
     * @returns {String}
     */
    buttonIcon() {
      let statusIcon

      switch (this.states.productForm) {
        case 'ready':
          statusIcon = 'icon-quick-add'
          break
        case 'loading':
          statusIcon = 'icon-spinner'
          break
        case 'adding':
          statusIcon = 'icon-spinner'
          break
        case 'added':
          statusIcon = 'icon-tick'
          break
        case 'error':
          statusIcon = 'icon-alert'
          break
        default:
          statusIcon = 'icon-quick-add'
      }

      return statusIcon
    },

    /**
     * Returns an accessible label for the carousels 'Next' button.
     * @returns {String}
     */
    carouselNextLabel() {
      return mint.strings.accessibility.carousel.next
    },

    /**
     * Returns an accessible label for the carousels 'Prev' button.
     * @returns {String}
     */
    carouselPrevLabel() {
      return mint.strings.accessibility.carousel.prev
    },

    /**
     * Calculate the 'Close' button text.
     * @returns {String}
     */
    closeButtonText() {
      return mint.strings.accessibility.close
    },

    /**
     * Calculate if to disable the 'Previous' button.
     * @returns {Boolean}
     */
    disableNextCarouselButton() {
      if (this.states.component !== 'ready') {
        return true
      }

      return this.mediaCarousel.lastSlideVisible
    },

    /**
     * Calculate if to disable the 'Previous' button.
     * @returns {Boolean}
     */
    disablePrevCarouselButton() {
      if (this.states.component !== 'ready') {
        return true
      }

      return this.mediaCarousel.firstSlideVisible
    },

    /**
     * Define if the Product Card should show in its PLP 'Trending' state.
     * @returns {Boolean}
     */
    isTrending() {

      // Global flag to enable / disable the Trending functionality.
      if (!mint.flags?.collections?.enableTrending)
        return false

      if (!this.trendingEnabled)
        return false

      if (!this.product.trendingBanner)
        return false

      return Boolean(Object.values(this.product.trendingBanner).length)
    },

    /**
     * Calculate responsive srcset `sizes` attribute for the images.
     * @returns {String}
     */
    imageSizes() {
      return `
        100vw,
        (min-width: ${breakpoints.large}) 50vw
      `
    },

    /**
     * Calculate the visual 'Trending' product media.
     * @returns {Boolean|Object}
     */
    trendingMedia() {
      if (!this.isTrending) return false

      const trendingMedia = {}
      const hasExternalVideo =
        Boolean(this.product.trendingBanner.video_external_url ||
          this.product.trendingBanner.video_external_desktop_url)
      const mediaType = hasExternalVideo ? 'video' : this.product.trendingBanner.media_type

      if (mediaType === 'image') {
        if (this.product.trendingBanner.image) {
          trendingMedia.image = {
            ...this.product.trendingBanner.image,
            type: 'image',
          }
        }

        if (this.product.trendingBanner.image_desktop) {
          trendingMedia.imageDesktop = {
            ...this.product.trendingBanner.image_desktop,
            type: 'image',
          }
        }

        trendingMedia.inlineMedia = true
      }

      if (mediaType === 'video') {
        if (this.product.trendingBanner.video) {
          trendingMedia.video = {
            ...this.product.trendingBanner.video,
            type: 'video',
          }
        }

        if (this.product.trendingBanner.video_desktop) {
          trendingMedia.videoDesktop = {
            ...this.product.trendingBanner.video_desktop,
            type: 'video',
          }
        }

        trendingMedia.autoplayVideo =
          this.product.trendingBanner.video_autoplay

        if (hasExternalVideo) {
          trendingMedia.videoExternalUrl = this.product.trendingBanner.video_external_url
          trendingMedia.videoExternalDesktopUrl = this.product.trendingBanner.video_external_desktop_url
        }

        trendingMedia.inlineMedia = false
      }
      
      return trendingMedia
    },

    /**
     * Calculate the primary image to display.
     * @returns {Boolean|Object}
     */
    primaryImage() {
      if (!this.product || !this.product.featuredImage) {
        return false
      }

      if (this.usePackshot) {
        const packShot = getPackShotImage(this.product.images)

        if (packShot) {
          return packShot.image
        }
      }

      return this.product.featuredImage
    },

    /**
     * Calculate the secondary image to display.
     * - Do not show gallery images on PLPs
     * - Do not show if only one image.
     * - Do not show second image on mobile devices.
     * @returns {Boolean|Object}
     */
    secondaryImage() {
      if (!mint.flags.productCards.enableHoverImage) {
        return false
      }

      return !this.product?.images || !this.product.images.length > 1 || this.isMobile
        ? false
        : this.product.images[1]
    },

    /**
     * Calculate if to show "Added to bag" overlay instead of opening Cart Drawer
     * @returns {Boolean}
     */
    showAddedOverlay() {
      if (!this.preventDrawer) {
        return false
      }

      return Boolean(this.states.addToCart === 'added')
    },

    /**
     * Calculate if to display the 'Label'.
     * - Do not show on mobile PLP.
     * @returns {Boolean}
     */
    showLabel() {
      if (
        !this.product.label ||
        (this.context !== 'default' && this.isMobile)
      ) {
        return false
      }

      return Boolean(this.product.label)
    },

    /**
     * Calculate if to show the button to query swatches.
     * @returns {Boolean}
     */
    showSwatchButton() {
      if (this.hideProductSwatches) {
        return false
      }

      if (
        !this.product?.handle ||
        (this.context === 'default' && this.isMobile)
      ) {
        return false
      }

      /**
       * If the product has been sourced from a collection or search using
       * the Storefront API, it will not include swatch data.
       * - In these cases, simply check for the existence of the swatch tag.
       * - The state check ensures that the button hides after swatches have
       *   been loaded.
       */
      if (
        this.getShopifyMerchandisingFlag() &&
        this.states.swatches !== 'ready'
      ) {
        return this.product.hasSwatchTag
      }

      return Boolean(
        this.states.swatches !== 'ready' &&
        this.product.linkedStyles?.length > 1,
      )
    },

    /**
     * Calculate if to show the swatch wrapper.
     * - this is done so as not to display an empty wrapper with a min-height when
     * no additional swatches are available
     * - the `:empty` pseudo-class does not fit due to the content shift when
     * request the swatches on click
     * @returns {Boolean}
     */
    showSwatchWrapper() {
      if (this.hideProductSwatches) {
        return false
      }

      if (
        !this.product?.handle ||
        (this.context === 'default' && this.isMobile)
      ) {
        return false
      }

      /**
       * If the product has been sourced from a collection or search using
       * the Storefront API, it will not include swatch data.
       * - In these cases, simply check for the existence of the swatch tag.
       * - This can cause singular swatches to be shown if the linked styles are
       *   unavailable.
       */
      if (this.getShopifyMerchandisingFlag()) {
        return this.product.hasSwatchTag
      }

      return Boolean(this.product.linkedStyles?.length > 1)
    },

    /**
     * Calculate if to show the 'Quick Add' button.
     * @returns {Boolean}
     */
    showQuickAdd() {
      if (this.product?.giftCard || this.context === 'cart-popup') {
        return false
      }

      return this.flags.enableQuickAdd
    },

    /**
     * Calculate if to show the variant selector.
     * @returns {Boolean}
     */
    showVariantSelector() {
      if (this.product?.giftCard) {
        return false
      }

      return Boolean(this.states.variantSelector === 'visible')
    },

    /**
     * Calculate if to show the `Wishlist` button.
     * @returns {Boolean}
     */
    showWishlistButton() {
      return !this.hideWishlistIcon
    },

    /**
     * Calculate the button to trigger the variant selector.
     * @returns {String}
     */
    selectVariantButton() {
      let defaultKey = 'selectVariantButton'

      if (this.states.productForm === 'loading') {
        return mint.strings.products.productForm.addingToCart
      }

      if (this.states.productForm === 'added') {
        return mint.strings.products.productForm.addedToCart
      }

      if (this.states.productForm === 'error') {
        return mint.strings.products.productForm.addedToCartError
      }

      /**
       * Default / 'ready' state.
       */
      if (!this.isMobile) {
        defaultKey = 'selectVariantButtonDesktop'
      }

      return mint.strings.products.productCard[defaultKey]
    },

    /**
     * Calculate the button to open the swatches.
     * @returns {String}
     */
    swatchButtonText() {
      return mint.strings.products.openSwatches
    },

    /**
     * Calculates whether the variant selector should overlay the full screen.
     * - used instead of CSS `@container` query due to Safari incompatibility
     * with `position: fixed`.
     * @returns {Boolean}
     */
    shouldOverlayVariantSelector() {
      return this.productCardWidth <= 270 || this.mobileDevice
    },

    /**
     * Calculate variant heading text (when product is added to cart).
     * - E.g: `Size 12 / Regular`
     * - Currently pulls locale strings from ShopTheLook, as it is only used there
     * @returns {String}
     */
    variantHeading() {
      return mint.Strings('shop_the_look.variant_heading', {
        replace: {
          variant: formatVariantTitle(this.lastAddedVariantTitle),
        },
      })
    },

    /**
     * Calculate 'View Bag' text (when product is added to cart).
     * - Currently pulls locale strings from ShopTheLook, as it is only used there
     * @returns {String}
     */
    viewBagLabel() {
      return mint.Strings('shop_the_look.view_bag')
    },
  },

  watch: {

    /**
     * A feature flag from the global state has been toggled.
     */
    featureFlags: {
      handler() {
        if (!Object.keys(this.featureFlags).length) {
          return
        }

        this.toggleFeatureFlags()
      },
      deep: true,
      immediate: true,
    },

    /**
     * The active Variant Selector in the parent components product grid has changed.
     */
    gridActiveVariantSelector() {
      if (this.gridActiveVariantSelector === this.product.handle) {
        return
      }

      this.states.variantSelector = 'hidden'
    },

    /**
     * Listen for if the device has been resized to mobile or desktop.
     */
    isMobile() {
      this.states.variantSelector = 'hidden'
    },

    /**
     * The user has resized their screen.
     * - Newer feature flag on collections works with `x_large` resolution,
     * so listen to all resizes
     */
    viewportWidth() {
      this.toggleFeatureFlags()
      this.setProductCardWidth()
      this.mobileDevice = !window.matchMedia(`(min-width: ${breakpoints.medium})`).matches
    },

    /**
     * The user has changed PLP layout.
     */
    columnGrid() {
      this.$nextTick(() => {
        this.setProductCardWidth()
      })
    },

    /**
     * Watch for the product object to set.
     * - Runs once on first change, once shallow objects are populated.
     */
    product() {
      if (this.states.component === 'ready' || !this.product?.handle) {
        return
      }

      this.currentVariant = this.product.variants[0]

      this.setProductAttributes()

      this.states.component = this.product?.handle ? 'ready' : 'loading'
    },

    /**
     * Watcher for `states.variantSelector`.
     *
     * - This watcher emits an event to notify the parent component when the variant
     * selector overlay is opened or closed.
     * - This is necessary for the parent (e.g. Shop The Look Popover) to re-apply
     * the `is-locked` class on `<body>`
     * - It has been added for cases when the variant-selector has an overlay,
     * especially when multiple modal-like components may be interacting at once.
     */
    'states.variantSelector': {
      handler(newValue) {
        if (!this.shouldOverlayVariantSelector) {
          return
        }

        if (newValue === 'visible') {
          document.body.classList.add('is-locked')
        }

        this.$emit('variant-selector-toggled', {
          handle: this.product.handle,
          state: newValue,
        })
      },
    },
  },

  created() {
    if (this.initialProduct) {
      this.product = this.getInitialProductObject()
    }
  },

  mounted() {
    this.toggleFeatureFlags()
    this.setMarketingDataSource()
    this.setAspectRatio()
    this.setProductCardWidth()
  },

  methods: {

    /**
     * Map Cart Actions.
     */
    ...mapActions(useCartStore, ['addItems']),

    /**
     * Map Wishlist methods.
     */
    ...mapActions(useWishlistStore, ['openBackInStock']),

    /**
     * Set custom attributes for the product.
     * - These are mostly used for event tracking.
     * - Products from the PLP will also have an 'attraqt' object.
     */
    setProductAttributes() {
      const attributes = this.product.attributes
      const source = this.trackingParameters

      this.product.attributes = {
        ...attributes,
        source,
      }
    },

    /**
     * If a product object is passed in, return that to be assigned to
     * this.product on `created`.
     * - Attraqt will return an image URL, not the image object (like Shopify).
     * - If the product source is Attraqt, then manually add the aspect
     * ratio of a product card to the image objects. This allows the image
     * component to correctly render without CLS.
     * @returns {Object}
     */
    getInitialProductObject() {
      if (this.product.source !== 'attraqt') {
        return this.initialProduct
      }

      const initialProduct = this.initialProduct

      initialProduct.images = initialProduct.images.map((image) => {
        return {
          ...image,
          aspectRatio: this.aspectRatio || '0.728',
        }
      })

      initialProduct.featuredImage = initialProduct.images[0]

      return initialProduct
    },

    /**
     * Return the state of the Shopify merchandising feature flag.
     * @returns {Boolean}
     */
    getShopifyMerchandisingFlag() {
      return this.isPlpProduct && mint?.flags?.shopifyMerchandising?.enable
    },

    /**
     * Toggle the enabled features for the Product Card.
     */
    toggleFeatureFlags() {
      if (!this.product?.handle) {
        return
      }

      if (!this.featureFlags.enableQuickAdd) {
        this.flags.enableQuickAdd = false

        return
      }

      let enableQuickAdd = true

      if (
        mint.request?.pageType === 'collection' ||
        mint.request?.pageType === 'search' ||
        this.isPlpProduct
      ) {
        enableQuickAdd = window.matchMedia(
          `(min-width: ${breakpoints.x_large})`,
        ).matches
          ? true
          : this.featureFlags.enableMobileQuickAddPlp
      }

      this.flags.enableQuickAdd = enableQuickAdd
    },

    /**
     * Toggles the visibility of the swatches.
     */
    async toggleSwatches() {
      try {
        await this.queryLinkedStyles()
        this.states.swatches = 'ready'
      } catch (error) {
        this.states.swatches = 'error'
      }
    },

    /**
     * Toggle the visibility of the variant selector.
     */
    async toggleVariantSelector() {

      /**
       * Linked Styles are additional products, so perform
       * a query to Shopify and merge the product objects together.
       */
      try {
        await this.queryLinkedStyles()

        this.states.variantSelector = this.states.variantSelector === 'visible'
          ? 'hidden'
          : 'visible'

        /**
         * Notify parent component.
         * - Used to ensure only one variant selector is opened at a time.
         */
        if (this.states.variantSelector === 'visible') {
          this.$emit('variant-selector-toggled', {
            handle: this.product.handle,
            state: 'visible',
          })
        }
      } catch (error) {
        this.states.variantSelector = 'error'
      }
    },

    /**
     * Query Storefront API for a given products Linked Styles.
     * - If the product has already been queried, return early.
     * - If Shopify merchandising is enabled, do not return early, as product
     *   objects sourced from collections & search will not include swatch data.
     * @return {Object}
     */
    queryLinkedStyles() {
      return new Promise((resolve, reject) => {
        if (
          this.product.source !== 'attraqt' &&
          !this.getShopifyMerchandisingFlag()
        ) {
          resolve(this.product)
          
          return
        }

        getProduct({
          handle: this.initialProduct.handle,
        })
          .then((response) => {
            this.product = {
              ...this.initialProduct,
              ...response,
              source: 'merge',
              wishlist: this.product.wishlist,
            }

            resolve(response)
          })
          .catch((error) => {
            this.states.productForm = 'error'

            mint.Log('error', 'Failed to fetch linkedStyles', error, {
              initialProduct: this.product.handle,
              queriedStyle: this.initialProduct.handle,
            })

            reject(error)
          })
      })
    },

    /**
     * Handle the adding of a product to the cart.
     */
    async handleAddToCart() {
      this.states.productForm = 'loading'
      this.states.addToCart = 'adding'
      this.lastAddedVariantTitle = this.currentVariant.title
      let settledFormState = 'ready'

      const items = {
        id: this.currentVariant.id,
        quantity: 1,
      }

      items.properties = getItemProperties(this.product, this.currentVariant)

      try {
        this.toggleVariantSelector()

        await this.addItems({
          data: items,
          meta: {
            ...this.product.attributes,
            source: {
              section: this.marketingDataSource,
            },
          },
          preventDrawer: this.preventDrawer,
        })

        this.states.productForm = 'added'
        this.states.addToCart = 'added'
        settledFormState = 'ready'

        this.updateCartPage()

        /**
         * Add Elevar 'Add To Cart' GTM data layer information.
         */
        setDataLayerValues({
          isPlpProduct: this.isPlpProduct,
          trackingParameters: this.trackingParameters?.section,
        })

      } catch (error) {
        settledFormState = 'error'

        mint.Log('fatal', 'Product Card: Failed to add product to the cart',
          error,
          {
            product: this.product,
            variant: this.currentVariant,
          },
        )

      } finally {
        setTimeout(() => {
          this.states.productForm = settledFormState
        }, 1000)

        setTimeout(() => {
          this.states.addToCart = settledFormState
        }, 3000)
      }
    },

    /**
     * Events for when the variant has changed, via the Variant Selector.
     * @param {Object} currentVariant - Formatted variant object.
     * @param {String} lastUpdatedOption - The most recent option that was changed.
     */
    handleVariantChange({ currentVariant, lastUpdatedOption }) {
      this.currentVariant = currentVariant

      /**
       * Existing UX of the product card enforces an automatic
       * 'Add to cart' when the user changes the 'Size' variant.
       */
      if (stringToHandle(lastUpdatedOption) === 'length') {
        return
      }

      /**
       * Variant is sold out and 'Back in stock' notifications are enabled.
       * - ensure that the `currentVariant` exists, as the `false` value also
       * passes the condition and triggers `BackInStock` popup accordingly
       */
      if (
        this.product?.wishlist?.bisEnabled &&
        (this.currentVariant && !this.currentVariant.available)
      ) {
        this.handleBackInStock()
        this.toggleVariantSelector()
        
        return
      }

      /**
       * Variant is sold out.
       */
      if (!this.currentVariant.available) {
        return
      }

      this.handleAddToCart()
    },

    /**
     * Trigger the Swym Wishlist 'Back In Stock' modal.
     * - helpers/wishlist.js was built with Liquid product objects in mind
     * - Therefore, rebuild a pseudo product object to pass over
     * - Wishlist will be replaced with a Pinia Store eventually.
     */
    async handleBackInStock() {
      try {
        await this.openBackInStock(this.product, this.currentVariant)
      } catch (error) {
        mint.Log('error', 'Could not open Back In Stock modal', error)
      }
    },

    /**
     * Handle events for clicking the link.
     * - Used to trigger 'Product Card Click' interaction events.
     * - IMPORTANT: Given the whole product card is a clickable link and also
     *   includes child elements with their own click events (Wishlist, Variant Selector)
     *   those click events need to have `@click.stop.prevent` to prevent
     *   the below from triggering unintentionally.
     */
    handleLinkClick() {

      // if (!mint.customer.canBeTracked) {
      //   return
      // }

      // const attributes = JSON.parse(JSON.stringify(this.product.attributes))
      // window.console.log('Track any Product Card Links here...', attributes)

      /**
       * Send click event to the parent component.
       * - Can be used for tracking events and such.
       * - Also used to preserve user scroll position on PLP 'Back' button.
       */
      this.$emit('link-clicked', {
        handle: this.product.handle,
        product: this.product,
      })

      if (this.trackingParameters.source === 'Bloomreach Recommendations') {
        const trackedEvent = {
          event: 'recommendation',
          data: {
            action: 'click',
            recommendation_id: this.trackingParameters.recommendation_id,
            item_id: Number(this.product.id),
          },
        }

        mint.EventBus.dispatch('bloomreach:trackEvent', trackedEvent)
      }
    },

    /**
     * Force close the `product-variant-selector` if child sizeDropdown was
     * closed.
     */
    handleSizeDropdownClose() {
      this.states.variantSelector = 'hidden'
    },

    /**
     * - Open the `Cart Drawer`
     * - Close any other popovers.
     */
    handleCartDrawerOpen() {
      this.states.addToCart = 'ready'

      mint.EventBus.dispatch('shopTheLookPopover:close')
      mint.EventBus.dispatch('cartDrawer:open')
    },

    /**
     * Callback used when product has been added to cart. If we are on the cart
     * page - reload the document to display changes.
     *
     * _**Note**_: Should be removed after cart rebuild.
     */
    updateCartPage() {
      if (mint.request.pageType !== 'cart') {
        return
      }
      window.location.reload()
    },

    /**
     * Set the marketing data source for Bloomreach.
     */
    setMarketingDataSource() {
      if (this.isPlpProduct) {
        this.marketingDataSource = window.mint.services.elevarSources.plpProductGrid

        return
      }

      this.marketingDataSource = this.trackingParameters?.section
    },

    /**
     * Overrides Aspect Ratio back to auto for Trending Products
     * @param {String} aspectRatio - Valid CSS aspect ratio
     */
    setAspectRatio(aspectRatio) {

      /**
       * - Avoid overwriting the aspect-ratio for the trending media as the
       * `setAspectRatio()` method with no param is triggered later than the
       * emitted event, causing the `undefined` value to be set.
       */
      if (this.isTrending && !aspectRatio) {
        return
      }

      /**
       * Enforce a fixed aspect ratio for all product cards.
       * The MediaElement component can dispatch a specific aspect ratio per image,
       * but assets supplied by creative are all inconsistently sized, creating
       * a visual mismatch in height between product cards.
       */
      let ratio = 0.728

      /**
       * Product Cards have a hard-set aspect ratio.
       * Only set aspect ratio if Trending product.
       */
      if (this.isTrending) {
        ratio = aspectRatio
      }

      this.$refs.productCard.style.setProperty(
        '--aspect-ratio',
        ratio,
      )
    },

    /**
     * Sets the current width of the product card.
     * - required for JS-based container logic since native `@container` queries
     * cannot be used with `position: fixed` in Safari just yet.
     */
    setProductCardWidth() {
      this.productCardWidth = this.$refs.productCard?.offsetWidth
    },
  },
}
</script>
